import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { oneLine } from 'common-tags';
import { isEmpty } from 'lodash';
import { Schema } from 'normalizr';
import { Middleware } from 'redux';

import { getNormalisationAction } from '../../normalisation/action-factory';
import { hasNormalisationAction } from '../../normalisation/utils';
import { defaultMiddlewareOptions } from './defaults';
import {
  ApiActionError,
  ApiActionResponse,
  ApiClient,
  APIMiddlewareWrapperFn,
  ApiMultiClient,
  CustomMiddlewareOptions
} from './types';
import {
  getClientName,
  getFulfilledAction,
  getPendingAction,
  getRejectedAction,
  getSchema,
  isApiRequest,
  mergeRequestConfig,
  normaliseData,
  preprocessRequest
} from './utils';

/**
 * Api Middleware to connect with Amazon API Gateway, processing the data before it is returned to the client.
 * @param {AxiosInstance} client
 * @param {Signer} signer
 * @param {Authenticator} authenticator
 * @param {Partial<CustomMiddlewareOptions>} customMiddlewareOptions
 * @returns {Middleware}
 */
export const APIMiddlewareWrapper: APIMiddlewareWrapperFn = (
  client,
  signer,
  authenticator,
  customMiddlewareOptions = {}
) => {
  const middlewareOptions: CustomMiddlewareOptions = { ...defaultMiddlewareOptions, ...customMiddlewareOptions };

  return APIMiddlewareMultiClientWrapper(
    {
      [middlewareOptions.clientName]: {
        client,
        signer,
        authenticator
      }
    },
    middlewareOptions
  );
};

/**
 * Axios Middlware multi-client.
 * @param {AxiosMultiClient} clients
 * @param {Partial<CustomMiddlewareOptions>} customMiddlewareOptions
 * @returns {Middleware}
 */
export const APIMiddlewareMultiClientWrapper = (
  clients: ApiMultiClient,
  customMiddlewareOptions: Partial<CustomMiddlewareOptions> = {}
): Middleware => {
  const middlewareOptions: CustomMiddlewareOptions = { ...defaultMiddlewareOptions, ...customMiddlewareOptions };
  const setupedClients: ApiMultiClient = {};

  /**
   * Axios Middleware to execute async call to external resources.
   * @returns {any}
   */
  return ({ dispatch }) => next => action => {
    if (!isApiRequest(action)) {
      return next(action);
    }

    const clientName: string = getClientName(action) || middlewareOptions.clientName;

    if (!clients[clientName]) {
      throw new Error(
        oneLine`Client with name '${clientName}' has not been defined in the APIMiddleware
        or it has not been added to the 'customMiddlewareOptions'`
      );
    }

    if (!setupedClients[clientName]) {
      setupedClients[clientName] = clients[clientName];
    }

    const setupedClient: ApiClient = setupedClients[clientName];

    const { client, signer, authenticator } = setupedClient;

    /**
     * Pass the pending action to the next middleware:
     * This object describes the pending state of a promise and could include
     * any data (for optimistic updates) and/or meta from the original action.
     */
    next(getPendingAction(action));

    const requestConfig: AxiosRequestConfig = mergeRequestConfig(client.defaults, action);

    /**
     * If there's a provided normalisation action provided along with the api request,
     * and if we have a schema, then we should flush whatever is needed.
     */
    if (hasNormalisationAction(action) && !isEmpty(getSchema(action))) {
      const normalisationAction = getNormalisationAction(action);
      if (normalisationAction) {
        dispatch(normalisationAction);
      }
    }

    return preprocessRequest(requestConfig, signer, authenticator).then(signedRequestConfig =>
      client.request(signedRequestConfig).then(
        (response: AxiosResponse): AxiosResponse => {
          const schema: Schema = getSchema(action);

          if (!isEmpty(schema)) {
            const normalisedData = normaliseData(response.data, schema);
            response.data = normalisedData;
          }

          const nextAction: ApiActionResponse = getFulfilledAction(action, response);

          /** Send the fulfilled action to the init of the middleware chain */
          dispatch(nextAction);

          return response;
        },
        (error: AxiosError): never => {
          const nextAction: ApiActionError = getRejectedAction(action, error);

          /** Send the rejected action to the init of the middleware chain */
          dispatch(nextAction);

          throw error;
        }
      )
    );
  };
};
